import { useEffect } from 'react';
import { fetchUserProfile } from 'common/src/api/users';
import useUserStore from 'common/src/stores/useUserStore';
import useDemoStore from 'common/src/stores/useDemoStore';
import { supabase } from 'common/src/supabase';

let authInitialized = false;
let initialSessionFetched = false;
let profileFetchInFlight = false;
let lastProfileAuthId: string | null = null;
let demoCheckInitialized = false;

const useAuthState = () => {
  const {
    setUserProfile,
    loadingProfile,
    setLoadingProfile,
    setSession,
    session,
    clearUserProfile,
    clearExporter,
  } = useUserStore();

  useEffect(() => {
    const fetchProfile = async (userId: string) => {
      if (profileFetchInFlight && lastProfileAuthId === userId) return;
      profileFetchInFlight = true;
      lastProfileAuthId = userId;
      try {
        const userData = await fetchUserProfile(userId);
        setUserProfile(userData);
      } catch (error: any) {
        // The profile query returns PGRST116 once Auth deletion clears auth_id.
        if (error?.code === 'PGRST116') {
          await supabase.auth.signOut();
          clearUserProfile();
          clearExporter();
          setSession(null);
        }
      } finally {
        profileFetchInFlight = false;
      }
    };

    // Only fetch initial session once per app load
    if (!initialSessionFetched) {
      initialSessionFetched = true;
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          setSession(session);
          fetchProfile(session.user.id).finally(() => setLoadingProfile(false));
        } else {
          setLoadingProfile(false);
          setSession(null);
        }
      });
    }

    // Ensure a single global subscription for auth state
    if (authInitialized) return;
    authInitialized = true;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        // Always keep the latest session in state
        setSession(session);

        // Only fetch profile on meaningful auth events, not on token refreshes.
        // MFA_CHALLENGE_VERIFIED: a superadmin with two-step verification can't
        // read their profile until the session is aal2, so fetch it again.
        if (
          event === 'SIGNED_IN' ||
          event === 'USER_UPDATED' ||
          event === 'MFA_CHALLENGE_VERIFIED'
        ) {
          fetchProfile(session.user.id).finally(() => setLoadingProfile(false));
          return;
        }

        // For other events like TOKEN_REFRESHED, avoid redundant profile fetches
        if (event === 'TOKEN_REFRESHED') {
          // Do not refetch profile on refresh to prevent request storms
          return;
        }

        // Fallback: ensure loading state is cleared if we haven't yet
        setLoadingProfile(false);
      } else {
        console.log('No session found');
        setLoadingProfile(false);
        setSession(null);
      }
    });

    // Don't unsubscribe to preserve a single global listener across mounts
    return () => {};
  }, [clearExporter, clearUserProfile, setSession, setUserProfile]);

  // Single global interval to check demo access
  const { demoExpired, demoAccessUntil } = useDemoStore();

  useEffect(() => {
    if (demoCheckInitialized) return;
    demoCheckInitialized = true;

    const CHECK_INTERVAL = 60 * 1000 * 5; // 5m

    const checkDemoAccess = async () => {
      const { userProfile } = useUserStore.getState();
      if (!userProfile?.id) return;
      try {
        const { data } = await supabase
          .from('users')
          .select('demo_access_until')
          .eq('id', userProfile.id)
          .single();
        const date = data?.demo_access_until || null;
        useDemoStore.getState().setDemoAccessUntil(date);
        if (date && new Date(date + 'T23:59:59') < new Date()) {
          useDemoStore.getState().setDemoExpired(true);
        }
      } catch (e) {
        // Silent fail — next interval will retry
      }
    };

    checkDemoAccess();
    setInterval(checkDemoAccess, CHECK_INTERVAL);
  }, []);

  return { session, loadingProfile, demoExpired, demoAccessUntil };
};

export default useAuthState;
